/**
 * ============================================================================
 * Session roster — the instructor verifies who was actually here
 * ============================================================================
 * The last link in the chain: participants scanned in and entered their own
 * details (Decision #58); this is where the person running the room confirms
 * they were really present.
 *
 * THIS IS THE ONE STAFF SCREEN THAT SHOWS NAMES AGAINST TOKENS. Everywhere else
 * participant performance data stays pseudonymous. `token_identity_link` carries
 * the tightest policy in the system (Decision #29) — the session's own
 * instructor, or a role holding identity.reveal, and nobody else. If this list
 * comes back empty for someone, that is the database working.
 *
 * VERIFYING IS A ONE-WAY DOOR. Attendance records are insert-once (M8): there is
 * no edit and no undo, because the alternative is a contact-hours figure that
 * can be quietly adjusted after the fact. The button says so before it is
 * pressed rather than explaining afterwards.
 */
import { useCallback, useEffect, useState } from 'react';
import { useParams, useLocation, Link } from 'react-router-dom';
import { apiFetch } from '../lib/api.js';
import ErrorBanner from '../components/ErrorBanner.jsx';
import MaterialsShelf from '../components/MaterialsShelf.jsx';
import BindSessionPanel from '../components/BindSessionPanel.jsx';
import CheckInCode from '../components/CheckInCode.jsx';
import SkillSheetMarker from '../components/SkillSheetMarker.jsx';
import { downloadAuthed } from '../lib/download.js';

function minutesLabel(minutes) {
  if (minutes === null || minutes === undefined) return null;
  const value = Number(minutes);
  if (value < 60) return `${Math.round(value)} min`;
  const hours = value / 60;
  return `${(Math.round(hours * 100) / 100).toFixed(2)} hr`;
}

function clockLabel(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

export function SessionRosterPage() {
  const { id } = useParams();

  const [session, setSession] = useState(null);
  const [rows, setRows] = useState(null);
  /**
   * undefined = not fetched yet · null = the fetch FAILED · object = loaded.
   *
   * Three states, not two. Initialising this to null made "still loading" and
   * "failed" indistinguishable, so the page flashed a red "the section's record
   * couldn't be loaded" on every first paint before correcting itself a second
   * later. Harmless in isolation and very much not harmless on a projector.
   */
  const [record, setRecord] = useState(undefined);
  /** The section's items and which were pushed — the "components" the roster is measured against. */
  const [itemsInfo, setItemsInfo] = useState(undefined);
  const [error, setError] = useState(null);
  const [busyToken, setBusyToken] = useState(null);
  /** Skill sheets (M44): the library, what has been marked here, who is being marked now. */
  const [skillTemplates, setSkillTemplates] = useState(undefined);
  const [skillSheets, setSkillSheets] = useState([]);
  const [marking, setMarking] = useState(null);
  const [certifying, setCertifying] = useState(false);
  const [closing, setClosing] = useState(false);
  /** Heads in the room, as typed by the instructor before it is saved. */
  const [headcountInput, setHeadcountInput] = useState('');
  const [savingHeadcount, setSavingHeadcount] = useState(false);
  /**
   * Completion proposals. Same three states as `record` and for the same
   * reason — a null that also means "failed" put a red banner on a projector.
   */
  const [completions, setCompletions] = useState(undefined);
  /** roster_id -> outcome, only where the instructor disagreed with the proposal. */
  const [overrides, setOverrides] = useState({});
  const [awarding, setAwarding] = useState(false);
  const [notice, setNotice] = useState(null);

  /**
   * Just the roster — what the poll refetches. Deliberately narrower than
   * load(): re-pulling the section record every two seconds would be three
   * requests a tick for data that cannot change during a session.
   */
  const loadRosterOnly = useCallback(async () => {
    const [roster, items] = await Promise.all([
      apiFetch(`/api/v1/sessions/${id}/roster`),
      apiFetch(`/api/v1/sessions/${id}/questions`).catch(() => null),
    ]);
    setRows(roster ?? []);
    if (items) setItemsInfo(items);
  }, [id]);

  const load = useCallback(async () => {
    const [detail, roster, items] = await Promise.all([
      apiFetch(`/api/v1/sessions/${id}`),
      apiFetch(`/api/v1/sessions/${id}/roster`),
      apiFetch(`/api/v1/sessions/${id}/questions`).catch(() => null),
    ]);
    setSession(detail);
    setRows(roster ?? []);
    setItemsInfo(items);

    // The certification rules live on the section, not the session, so they are
    // read from the record. Fetched separately because a failure here must not
    // stop the instructor seeing who is in the room.
    if (detail.class_version == null) {
      // Unbound impromptu session (M28): no published section yet, so no
      // record to read the certification rules from.
      setRecord(null);
      return;
    }
    try {
      const rec = await apiFetch(`/api/v1/classes/${detail.class_id}/attendance-record`);
      setRecord(rec);
    } catch {
      setRecord(null); // genuinely failed — distinct from the initial undefined
    }
  }, [id]);

  /**
   * LIVE ROSTER — polled every 2 seconds, deliberately not sockets.
   *
   * Thirty seconds of names appearing as people scan is the whole demo, so this
   * has to work on conference-room Wi-Fi with one person on cellular. Polling is
   * the boring choice and the right one here: nothing to reconnect, nothing to
   * resubscribe after a laptop sleeps, and a dropped request self-heals two
   * seconds later.
   *
   * Sockets already exist in backend/src/sockets, but H1 left a known gap —
   * `session:join` authorises presence of an identity, not entitlement to that
   * specific session. Fine for a room we control, not something to lean on in
   * front of the people who decide. Revisit for the pilot, with that gap closed.
   *
   * A failed poll is swallowed: a blink of connectivity must not replace a
   * populated roster with an error banner mid-demo. The last good list stays.
   */
  useEffect(() => {
    let active = true;
    load().catch((err) => active && setError(err));

    const timer = setInterval(() => {
      if (!active) return;
      loadRosterOnly().catch(() => {});
    }, 2000);

    return () => {
      active = false;
      clearInterval(timer);
    };
  }, [load, loadRosterOnly]);

  async function verify(token) {
    setError(null);
    setBusyToken(token);
    try {
      await apiFetch(`/api/v1/sessions/${id}/attendance`, {
        method: 'POST',
        body: JSON.stringify({ token }),
      });
      await load();
    } catch (err) {
      setError(err);
    } finally {
      setBusyToken(null);
    }
  }

  const loadCompletions = useCallback(async () => {
    try {
      setCompletions(await apiFetch(`/api/v1/sessions/${id}/completions`));
    } catch {
      setCompletions(null);
    }
  }, [id]);

  /**
   * Award what is on screen. Only the disagreements are sent — an empty body
   * means "accept every proposal", so the request says exactly what the person
   * clicking changed and nothing else.
   */
  async function award() {
    setError(null);
    setNotice(null);
    setAwarding(true);
    try {
      const awards = Object.entries(overrides).map(([roster_id, outcome]) => ({ roster_id, outcome }));
      const result = await apiFetch(`/api/v1/sessions/${id}/completions`, {
        method: 'POST',
        body: JSON.stringify(awards.length ? { awards } : {}),
      });
      setNotice(
        `Awarded ${result.awarded} completion${result.awarded === 1 ? '' : 's'} — ` +
          `${result.complete} complete, ${result.incomplete} incomplete.` +
          (result.not_at_this_session
            ? ` ${result.not_at_this_session} enrolled ${result.not_at_this_session === 1 ? 'person was' : 'people were'} not at this session and ${result.not_at_this_session === 1 ? 'was' : 'were'} left off its record.`
            : '') +
          ' These records are permanent.'
      );
      setOverrides({});
      await loadCompletions();
    } catch (err) {
      setError(err);
    } finally {
      setAwarding(false);
    }
  }

  /**
   * End the class. Closing revokes every outstanding token — that is what
   * actually stops further check-ins — and is deliberately NOT certification
   * and NOT awarding (close-is-not-completion). Verify, close, certify, award.
   */
  async function closeSession() {
    setError(null);
    setNotice(null);
    setClosing(true);
    try {
      const result = await apiFetch(`/api/v1/sessions/${id}/close`, { method: 'POST' });
      setNotice(
        `Closed. ${result.tokens_revoked ?? 0} check-in token${result.tokens_revoked === 1 ? '' : 's'} revoked; ` +
          `nobody else can scan in.${result.note ? ` ${result.note}` : ''}`
      );
      await load();
    } catch (err) {
      setError(err);
    } finally {
      setClosing(false);
    }
  }

  /**
   * THE OTHER HALF OF THE TIME GATE. The window stops a shared code being used
   * after the class; the head count catches one being used during it from
   * outside the room. The instructor counts heads, the screen puts the number
   * beside the check-ins and says what the difference means.
   */
  async function saveHeadcount() {
    setError(null);
    setSavingHeadcount(true);
    try {
      const updated = await apiFetch(`/api/v1/sessions/${id}/headcount`, {
        method: 'POST',
        body: JSON.stringify({ headcount: Number(headcountInput) }),
      });
      setSession(updated);
      setHeadcountInput('');
    } catch (err) {
      setError(err);
    } finally {
      setSavingHeadcount(false);
    }
  }

  async function certify() {
    setError(null);
    setNotice(null);
    setCertifying(true);
    try {
      await apiFetch(`/api/v1/sessions/${id}/certify`, { method: 'POST' });
      setNotice('Certified. This session’s record is now official and cannot be edited.');
      await load();
    } catch (err) {
      setError(err);
    } finally {
      setCertifying(false);
    }
  }

  const timing = record?.certification_timing;
  const statement = record?.certification_statement;
  const thisSessionCertified = (record?.sessions ?? []).find((s) => s.id === id)?.certified ?? false;

  // Completions only exist after certification (M7 puts that gate in the
  // database), so there is nothing to fetch until the signature is on it.
  useEffect(() => {
    if (thisSessionCertified) loadCompletions();
  }, [thisSessionCertified, loadCompletions]);
  // Only people who actually turned up can be unverified. An open seat is
  // nobody, and counting it here would nag the instructor about a no-show.
  const unverified = (rows ?? []).filter((r) => r.checked_in && !r.attendance).length;

  // WHO NEEDS THE INSTRUCTOR'S ATTENTION before the roster is verified: anyone
  // checked in who missed a pushed item, with the final assessment called out
  // separately because that one decides credit (M21).
  const pushedCount = (itemsInfo?.questions ?? []).filter((q) => q.pushed_at).length;
  const checkDefined = (itemsInfo?.questions ?? []).some((q) => q.is_completion_check);
  const attention = (rows ?? []).filter((r) => r.checked_in && r.components && r.components.missed.length > 0);
  const missedFinal = attention.filter((r) => r.components.completion_check.complete === false);
  const isOpen = session?.status === 'active';
  const isClosed = session?.status === 'closed';

  const loadSkills = useCallback(async () => {
    try {
      const [templates, sheets] = await Promise.all([
        apiFetch('/api/v1/skill-sheets/templates'),
        apiFetch(`/api/v1/sessions/${id}/skill-sheets`),
      ]);
      setSkillTemplates(templates ?? []);
      setSkillSheets(sheets ?? []);
    } catch {
      setSkillTemplates(null); // the roster still works without them
    }
  }, [id]);
  useEffect(() => {
    loadSkills();
  }, [loadSkills]);
  const sheetsByToken = new Map();
  for (const k of skillSheets) sheetsByToken.set(k.token, [...(sheetsByToken.get(k.token) ?? []), k]);
  const canMarkSkills = (isOpen || isClosed) && (skillTemplates?.length ?? 0) > 0;

  // The board's "Certify" / "Award completions" buttons land here with
  // #close-out; scroll to it once the page has something to scroll to.
  const { hash } = useLocation();
  useEffect(() => {
    if (hash !== '#close-out' || !session) return;
    document.getElementById('close-out')?.scrollIntoView({ block: 'start' });
  }, [hash, session]);

  const checkedIn = (rows ?? []).filter((r) => r.checked_in).length;
  const awaiting = (rows ?? []).filter((r) => r.person?.awaiting_claim).length;
  const verified = (rows ?? []).filter((r) => r.attendance).length;
  const totalMinutes = (rows ?? []).reduce(
    (sum, r) => sum + Number(r.attendance?.contact_minutes ?? 0),
    0
  );

  return (
    <>
      <p className="crumb">
        <Link to="/sections">← All sections</Link>
      </p>

      <div className="titlebar">
        <h1>Who’s here</h1>
        {/* Present belongs inside the running class, not back out through the
            Sessions list (Brian, 22 Sep rehearsal). Same screen, one click. */}
        {session?.status === 'active' && (
          <Link className="btn btn--primary btn--auto" to={`/sessions/${id}/present`}>
            Present ▶
          </Link>
        )}
      </div>
      <p className="muted">
        {session
          ? `${session.location ?? 'No location set'} · ${new Date(
              session.scheduled_start
            ).toLocaleString()} · ${session.status}`
          : 'Loading…'}
      </p>
      {/* The gate, stated. The code only works inside this window and the
          window follows the schedule — that is the audit story, so it is on
          the screen rather than in a manual. */}
      {session && session.status !== 'canceled' && (
        <p className="muted">
          Check-in window: {clockLabel(session.qr_valid_from)}–{clockLabel(session.qr_valid_until)}. Codes
          do not work outside it, and the window only moves if the schedule is changed before class.
        </p>
      )}

      <ErrorBanner error={error} />
      {notice && (
        <p className="banner banner--success" role="status">
          {notice}
        </p>
      )}

      {/* Path B (M28): run first, build the section afterwards. */}
      {session?.is_impromptu && session.class_version == null && (
        <BindSessionPanel session={session} onBound={() => load().catch(setError)} />
      )}

      {/* The section's shelf, one tap away while the room is live (M26). */}
      {session?.class_id && (
        <details className="panel shelf__panel" open={session.status === 'active'}>
          <summary>
            <strong>Materials</strong> <span className="muted">— slides, video, handouts for this class</span>
          </summary>
          <MaterialsShelf classId={session.class_id} compact />
        </details>
      )}

      {/* The code comes first in the running order: launch the class, put this on
          the wall, then everything below happens. */}
      {!isClosed && session?.status !== 'canceled' && (
        <CheckInCode sessionId={id} sessionStatus={session?.status} />
      )}

      {/* RUN OF SHOW. The panels below are in the order the class actually
          happens: who's here and took part → verify → close the class →
          certify → award. Certification used to sit at the top, where it was
          the first thing on screen and the last thing that should be done. */}

      {/* Who needs the instructor's attention BEFORE the roster is verified.
          Missing the final assessment means no credit (M21); missing anything
          else is the troubleshooting conversation, had in the room. */}
      {rows && rows.length > 0 && pushedCount > 0 && (
        <section className="panel" aria-labelledby="attention-heading">
          <h2 id="attention-heading">Took part</h2>
          <p className="muted">
            {pushedCount} of {itemsInfo?.questions?.length ?? 0} items have been put to the room
            {checkDefined ? '' : ' — this section has no completion check, so completion follows seat time alone'}
            .
          </p>
          {attention.length === 0 ? (
            <p className="callout callout--ok">
              <strong>Everyone checked in has answered everything asked so far.</strong>
            </p>
          ) : (
            <>
              {missedFinal.length > 0 && (
                <p className="callout callout--warn">
                  <strong>
                    {missedFinal.length} {missedFinal.length === 1 ? 'person has' : 'people have'} not done
                    the end-of-session assessment.
                  </strong>
                  <span className="callout__detail">
                    They will be proposed <em>incomplete</em> — no credit — unless you decide otherwise
                    when awarding. If they are still in the room, that is the conversation to have
                    now.
                  </span>
                </p>
              )}
              <ul className="plain-list">
                {attention.map((r) => (
                  <li key={r.enrollment_id}>
                    <strong>{r.person?.full_name}</strong>
                    <span className="muted">
                      {' '}
                      — answered {r.components.answered} of {r.components.pushed}; missed{' '}
                      {r.components.missed
                        .map((m) => `item ${m.position}${m.is_completion_check ? ' (final assessment)' : ''}`)
                        .join(', ')}
                    </span>
                  </li>
                ))}
              </ul>
            </>
          )}
        </section>
      )}

      {/* CLOSE. Ends the class and revokes every token — the act that stops
          further check-ins. Not certification, not awarding. */}
      {isOpen && (
        <section className="panel" aria-labelledby="close-heading">
          <h2 id="close-heading">Close the class</h2>
          <p className="muted">
            Verify everyone first — closing revokes the check-in codes, so nobody else can scan in
            afterwards. Certifying and awarding are separate steps and come next.
          </p>
          {unverified > 0 && (
            <p className="callout callout--warn">
              <strong>
                {unverified} {unverified === 1 ? 'person is' : 'people are'} checked in but not yet
                verified.
              </strong>
              <span className="callout__detail">
                You can still close; they will appear on the record with no contact hours.
              </span>
            </p>
          )}
          <button type="button" className="btn btn--primary btn--auto" onClick={closeSession} disabled={closing}>
            {closing ? 'Closing…' : 'Close the class'}
          </button>
        </section>
      )}

      <section className="panel" id="close-out" aria-labelledby="certify-heading">
        <h2 id="certify-heading">Certification of accuracy</h2>

        {thisSessionCertified ? (
          <p className="callout callout--ok">
            <strong>This session is certified.</strong>
            <span className="callout__detail">
              Its record is official. A certification cannot be edited — a change goes through a
              logged correction and re-certification.
            </span>
          </p>
        ) : (
          <>
            {statement && <blockquote className="attestation__text">“{statement.text}”</blockquote>}

            {/* Until the record loads we do not know whether signing is allowed.
                Rendering a DISABLED button in that gap looked identical to a
                live one and silently swallowed the click — and if the record
                fetch failed it stayed dead forever with no explanation. Say
                what is happening instead. */}
            {!isClosed ? (
              <p className="callout callout--warn">
                <strong>Close the class first.</strong>
                <span className="callout__detail">
                  Certification signs for the attendance as it finally stood, so the class has to be
                  over before anyone can sign for it.
                </span>
              </p>
            ) : !timing ? (
              record === null ? (
                <p className="callout callout--warn">
                  <strong>Can’t check the certification rules right now.</strong>
                  <span className="callout__detail">
                    The section’s record couldn’t be loaded, so signing is unavailable. Reload the
                    page; if it persists, the section may not be published.
                  </span>
                </p>
              ) : (
                <p className="muted" aria-busy="true">
                  Checking whether this can be certified yet…
                </p>
              )
            ) : !timing.allowed ? (
              <p className="callout callout--warn">
                <strong>Not yet.</strong>
                <span className="callout__detail">
                  {timing.explanation} Certifying before then would attest to something that
                  hasn’t happened yet.
                </span>
              </p>
            ) : (
              <>
                {unverified > 0 && (
                  <p className="callout callout--warn">
                    <strong>
                      {unverified} {unverified === 1 ? 'person has' : 'people have'} not been
                      verified yet.
                    </strong>
                    <span className="callout__detail">
                      They’ll appear on the record with no contact hours. Verify them first if
                      they were here.
                    </span>
                  </p>
                )}
                <p className="muted">
                  Signing is permanent and records your name, the time, and the exact wording
                  above.
                </p>
                <button
                  type="button"
                  className="btn btn--primary btn--auto"
                  onClick={certify}
                  disabled={certifying}
                >
                  {certifying ? 'Signing…' : 'Certify this session'}
                </button>
              </>
            )}
          </>
        )}
      </section>


      {/* COMPLETIONS. Appears only once the session is certified, because the
          database will not accept a completion for an uncertified session (M7)
          and a button that can only fail is worse than no button. */}
      {thisSessionCertified && (
        <section className="panel" aria-labelledby="completions-heading">
          <h2 id="completions-heading">Completions</h2>

          {completions === undefined && (
            <p className="muted" aria-busy="true">
              Loading…
            </p>
          )}
          {completions === null && (
            <p className="callout callout--warn">The completion list couldn’t be loaded.</p>
          )}

          {completions && (
            <>
              <p className="muted">
                What each person earned. The system proposes from verified attendance; you decide.
                Attendance and outcome are not the same question — somebody can sit the whole
                session and still not have met the objectives.
              </p>

              {completions.blocked_reason && (
                <p className="callout callout--ok">
                  <strong>Nothing left to award.</strong>
                  <span className="callout__detail">{completions.blocked_reason}</span>
                </p>
              )}

              <div className="schedule__scroll">
                <table className="schedule__table">
                <thead>
                  <tr>
                    <th scope="col">Person</th>
                    <th scope="col">Attendance</th>
                    <th scope="col">Award</th>
                  </tr>
                </thead>
                <tbody>
                  {completions.rows.map((r) => {
                    const done = r.already_awarded;
                    const outcome = overrides[r.roster_id] ?? r.outcome;
                    return (
                      <tr key={r.roster_id}>
                        <td>
                          <strong>{r.full_name ?? 'Open seat'}</strong>
                          {r.agency_name && <div className="muted">{r.agency_name}</div>}
                        </td>
                        <td>
                          {r.contact_minutes == null ? (
                            <span className="muted">not verified</span>
                          ) : (
                            <>
                              {r.contact_minutes} min
                              {r.short_by_minutes > 0 && (
                                <div className="muted">{r.short_by_minutes} min under</div>
                              )}
                            </>
                          )}
                        </td>
                        <td>
                          {done ? (
                            <span className="badge badge--published">
                              {done.outcome === 'complete' ? `${done.ce_hours} h awarded` : 'incomplete'}
                            </span>
                          ) : !r.at_this_session && !(r.roster_id in overrides) ? (
                            // Enrolled on the section, not here today (another
                            // date of a multi-date section, or a no-show). Not
                            // written unless the instructor decides them.
                            <>
                              <span className="muted">Not at this session — left off this record</span>
                              <div>
                                <button
                                  type="button"
                                  className="btn btn--quiet"
                                  onClick={() => setOverrides((o) => ({ ...o, [r.roster_id]: 'incomplete' }))}
                                >
                                  Record as a no-show
                                </button>
                              </div>
                            </>
                          ) : (
                            <>
                              <select
                                aria-label={`Outcome for ${r.full_name ?? 'this seat'}`}
                                value={outcome}
                                onChange={(e) =>
                                  setOverrides((o) => ({ ...o, [r.roster_id]: e.target.value }))
                                }
                              >
                                <option value="complete">
                                  Complete{r.outcome === 'complete' ? ` · ${r.ce_hours} h` : ''}
                                </option>
                                <option value="incomplete">Incomplete · 0 h</option>
                              </select>
                              <div className="muted">{r.reason}</div>
                            </>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                </table>
              </div>

              {completions.can_award && (
                <>
                  <p className="callout callout--warn">
                    <strong>A completion cannot be taken back.</strong>
                    <span className="callout__detail">
                      These records are permanent. A mistake is corrected by superseding it, which
                      leaves both entries in the file for good — so check the list before awarding.
                    </span>
                  </p>
                  <button
                    type="button"
                    className="btn btn--primary"
                    onClick={award}
                    disabled={awarding}
                  >
                    {awarding ? 'Awarding…' : 'Award completions'}
                  </button>
                </>
              )}
            </>
          )}
        </section>
      )}

      <section className="panel" aria-labelledby="tally-heading">
        <h2 id="tally-heading">Tally</h2>
        <dl className="detail-list">
          <div>
            <dt>Checked in</dt>
            <dd>
              {rows ? `${checkedIn} of ${rows.length}` : '—'}
              {awaiting > 0 && (
                <span className="muted"> · {awaiting} seat{awaiting === 1 ? '' : 's'} still open</span>
              )}
            </dd>
          </div>
          <div>
            <dt>Heads in the room</dt>
            <dd>
              {session?.headcount == null ? (
                <span className="muted">not counted yet</span>
              ) : (
                <>
                  {session.headcount}
                  {rows && session.headcount !== checkedIn && (
                    <span className="muted">
                      {' '}
                      · {checkedIn > session.headcount
                        ? `${checkedIn - session.headcount} more check-in${checkedIn - session.headcount === 1 ? '' : 's'} than people — a code was used from outside the room`
                        : `${session.headcount - checkedIn} in the room ${session.headcount - checkedIn === 1 ? 'has' : 'have'} not checked in`}
                    </span>
                  )}
                  {rows && session.headcount === checkedIn && <span className="muted"> · matches check-ins</span>}
                </>
              )}
              {(isOpen || isClosed) && !thisSessionCertified && (
                <form
                  className="schedule__actions"
                  onSubmit={(e) => {
                    e.preventDefault();
                    saveHeadcount();
                  }}
                >
                  <input
                    type="number"
                    min="0"
                    inputMode="numeric"
                    aria-label="Heads counted in the room"
                    placeholder="Count the room…"
                    value={headcountInput}
                    onChange={(e) => setHeadcountInput(e.target.value)}
                    disabled={savingHeadcount}
                    style={{ width: '10rem' }}
                  />
                  <button type="submit" className="btn btn--auto" disabled={savingHeadcount || headcountInput === ''}>
                    {savingHeadcount ? 'Saving…' : session?.headcount == null ? 'Record head count' : 'Recount'}
                  </button>
                </form>
              )}
            </dd>
          </div>
          <div>
            <dt>Verified present</dt>
            <dd>{rows ? `${verified} of ${checkedIn}` : '—'}</dd>
          </div>
          <div>
            <dt>Contact hours recorded</dt>
            <dd>
              {rows ? `${(Math.round((totalMinutes / 60) * 100) / 100).toFixed(2)} hr` : '—'}
              <span className="muted"> · 60 minutes = 1 hour</span>
            </dd>
          </div>
        </dl>
        <p className="muted">
          For a non-regularly scheduled section this total is the funding claim, so it is
          computed from the recorded times rather than typed in.
        </p>
      </section>

      {marking && (
        <SkillSheetMarker
          sessionId={id}
          person={marking}
          templates={skillTemplates}
          onCancel={() => setMarking(null)}
          onSaved={async () => {
            setMarking(null);
            await loadSkills();
          }}
        />
      )}

      <section className="panel" aria-labelledby="roster-heading">
        <h2 id="roster-heading">Checked in</h2>

        {rows === null && !error && (
          <p className="muted" aria-busy="true">
            Loading…
          </p>
        )}

        {rows !== null && rows.length === 0 && (
          <p className="muted">
            Nobody is enrolled in this section yet.
          </p>
        )}

        {rows !== null && rows.length > 0 && (
          <div className="schedule__scroll">
            <table className="schedule__table">
              <thead>
                <tr>
                  <th scope="col">#</th>
                  <th scope="col">Name</th>
                  <th scope="col">Student ID</th>
                  <th scope="col">Scanned in</th>
                  <th scope="col">Took part</th>
                  <th scope="col">Skills</th>
                  <th scope="col">Contact time</th>
                  <th scope="col">
                    <span className="visually-hidden">Verify</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, index) => (
                  <tr key={row.enrollment_id} className={row.person?.awaiting_claim ? 'row--awaiting' : undefined}>
                    <td className="muted">{index + 1}</td>
                    <td>
                      {row.person?.awaiting_claim ? (
                        <span className="muted">{row.person.full_name} — open seat</span>
                      ) : (
                        row.person?.full_name ?? '—'
                      )}
                    </td>
                    <td>
                      {row.person?.identity_status === 'unconfirmed' ? (
                        <span className="badge badge--cancelled" title="Registered without an A-B Tech ID — the office confirms it afterwards">
                          unconfirmed
                        </span>
                      ) : row.person?.external_id ? (
                        <code>{row.person.external_id}</code>
                      ) : (
                        <span className="muted">—</span>
                      )}
                    </td>
                    <td>{row.checked_in ? clockLabel(row.checked_in_at) : <span className="muted">—</span>}</td>
                    <td>
                      {!row.checked_in || !row.components ? (
                        <span className="muted">—</span>
                      ) : (
                        <>
                          {row.components.answered} of {row.components.pushed}
                          {row.components.completion_check.complete === true && (
                            <span className="badge badge--published"> final ✓</span>
                          )}
                          {row.components.completion_check.complete === false && (
                            <span className="badge badge--cancelled"> final missing</span>
                          )}
                        </>
                      )}
                    </td>
                    <td>
                      {(sheetsByToken.get(row.token) ?? []).map((k) => (
                        <div key={k.id}>
                          <button
                            type="button"
                            className="btn btn--quiet btn--auto"
                            title="Download the completed sheet (PDF)"
                            onClick={() =>
                              downloadAuthed(
                                `/api/v1/skill-sheets/${k.id}/pdf`,
                                `Skill_sheet_${k.skill_sheet_templates?.code ?? 'sheet'}_${String(row.person?.full_name ?? '').replace(/[^A-Za-z0-9]+/g, '_')}.pdf`
                              ).catch(setError)
                            }
                          >
                            {k.skill_sheet_templates?.code ?? k.skill_name}
                          </button>{' '}
                          <span className={k.result === 'PASS' ? 'badge badge--published' : 'badge badge--cancelled'}>
                            {k.result === 'PASS' ? 'Pass' : k.result === 'FAIL_CRITICAL' ? 'Fail (critical)' : 'Remediate'}
                          </span>
                        </div>
                      ))}
                      {row.checked_in && row.token && canMarkSkills && (
                        <button
                          type="button"
                          className="btn btn--auto"
                          onClick={() => {
                            setMarking({ token: row.token, full_name: row.person?.full_name, role_title: row.role_title });
                            setTimeout(() => document.getElementById('marker-heading')?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
                          }}
                          disabled={Boolean(marking)}
                        >
                          Skill sheet<span className="visually-hidden"> — {row.person?.full_name}</span>
                        </button>
                      )}
                      {!row.checked_in && <span className="muted">—</span>}
                    </td>
                    <td>
                      {row.attendance ? (
                        <>
                          <span className="badge badge--published">
                            {minutesLabel(row.attendance.contact_minutes) ?? 'Recorded'}
                          </span>
                          <span className="muted">
                            {' '}
                            {clockLabel(row.attendance.time_in)}–{clockLabel(row.attendance.time_out)}
                          </span>
                        </>
                      ) : (
                        <span className="muted">Not verified</span>
                      )}
                    </td>
                    <td>
                      {row.attendance ? (
                        <span className="muted">✓ Verified</span>
                      ) : !row.checked_in ? (
                        <span className="muted">—</span>
                      ) : (
                        <button
                          type="button"
                          className="btn"
                          disabled={busyToken === row.token}
                          onClick={() => verify(row.token)}
                        >
                          {busyToken === row.token ? 'Recording…' : 'Was here'}
                          <span className="visually-hidden"> — {row.person?.full_name}</span>
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <p className="muted">
          Verifying records the time from their scan to now, and <strong>cannot be undone</strong> —
          attendance records are permanent by design, so a change has to go through a logged
          correction. Verify people as they finish.
        </p>
      </section>
    </>
  );
}

export default SessionRosterPage;
